(function () {
  "use strict";

  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const sym = (window.themeCurrency && window.themeCurrency.symbol) || "£";
  const money = (cents) => sym + Number((cents || 0) / 100).toLocaleString("en-GB", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const ready = (fn) => document.readyState !== "loading" ? fn() : document.addEventListener("DOMContentLoaded", fn);
  const cssEscape = (value) => window.CSS && CSS.escape ? CSS.escape(String(value)) : String(value).replace(/"/g, '\\"');
  /* declared at the top: ready() below can run (and call syncFromCart) before
     the rest of this IIFE is evaluated, so a late `let` would still be in its
     temporal dead zone when syncFromCart first reads it */
  let syncRunning = false;

  ready(() => {
    setupProductPage();
    setupServicesAndAddons();
    /* Cart changes made elsewhere (main product added / removed, qty edits on
       the /cart page) can orphan service & add-on lines — re-clean and re-sync
       the checkboxes whenever the cart changes anywhere on the site. */
    document.addEventListener("cosynest:cart-changed", () => { syncFromCart(); });
    document.addEventListener("shopify:section:load", () => {
      setupProductPage(true);
      setupServicesAndAddons(true);
    });
  });

  async function cartPost(url, body) {
    const res = await fetch(url, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error("Cart request failed");
    return res.json().catch(() => null);
  }

  async function refreshCart(open) {
    try {
      const [cart, section] = await Promise.all([
        fetch("/cart.js", { credentials: "same-origin" }).then((r) => r.json()),
        fetch("/cart?section_id=cart-drawer", { credentials: "same-origin" }).then((r) => r.text()),
      ]);
      ["#cartCount", "#cartCount2"].forEach((sel) => {
        const el = $(sel);
        if (el) el.textContent = cart.item_count || 0;
      });
      const doc = new DOMParser().parseFromString(section, "text/html");
      const fresh = doc.querySelector("#cartDrawer");
      const current = document.getElementById("cartDrawer");
      /* Re-rendered markup arrives closed: preserve the open state across the
         swap so qty/remove inside the drawer never snaps it shut. */
      if (fresh && current) {
        if (current.classList.contains("is-open")) {
          fresh.classList.add("is-open");
          fresh.setAttribute("aria-hidden", "false");
        }
        current.replaceWith(fresh);
      }
      if (open && fresh) openCart();
    } catch (err) {
      console.warn("[cosynest]", err);
    }
  }

  function openCart() {
    const cart = $("#cartDrawer");
    const overlay = $("#overlay");
    if (!cart) return;
    cart.classList.add("is-open");
    cart.setAttribute("aria-hidden", "false");
    if (overlay) overlay.classList.add("is-on");
    document.body.classList.add("no-scroll");
  }

  function toast(message) {
    const el = $("#toast");
    if (!el) return;
    el.textContent = message;
    el.classList.add("is-show");
    clearTimeout(toast.timer);
    toast.timer = setTimeout(() => el.classList.remove("is-show"), 2400);
  }

  function setupProductPage(force) {
    const root = $("[data-product-section]");
    if (!root || (root.dataset.productServicesReady === "1" && !force)) return;
    root.dataset.productServicesReady = "1";

    const jsonEl = root.querySelector("[data-variants]");
    let variants = [];
    if (jsonEl) {
      try { variants = JSON.parse(jsonEl.textContent || "[]"); }
      catch (e) { variants = []; }
    }
    const rows = $$("[data-option-row]", root);
    const selected = {};
    rows.forEach((row) => {
      const active = row.querySelector(".is-active[data-value]") || row.querySelector("[data-value]");
      selected[row.dataset.optionRow] = active ? active.dataset.value : null;
    });
    syncStorageOptionsForSize(root, rows, selected);
    setupVariantTabs(root);
    setupPdpTabs(root);
    setupPdpAccordions(root);
    setupColorSwatches(root);
    setupPdpGallery(root);

    const idInput = root.querySelector('form[data-product-form] input[name="id"]');
    const qtyInput = root.querySelector('form[data-product-form] input[name="quantity"]');
    const qtyVal = $("#pdpQty");
    let qty = parseInt(qtyInput && qtyInput.value, 10) || 1;

    const currentVariant = () =>
      variants.find((v) => rows.every((row) => v["option" + row.dataset.optionRow] === selected[row.dataset.optionRow])) || variants[0];

    const render = () => {
      const v = currentVariant();
      if (!v) return;
      if (idInput) idInput.value = v.id;
      const compare = v.compare_at_price || 0;
      const onSale = compare > v.price;
      const priceNow = $("#pdpPriceNow");
      const priceWas = $("#pdpPriceWas");
      const priceSave = $("#pdpPriceSave");
      const klarna = $("#pdpKlarna");
      const split = root.querySelector("[data-split-price]");
      const total = $("#pdpTotal");
      const stickyPrice = $("#pdpStickyPrice");
      const stock = $("#pdpStock");
      const add = root.querySelector("[data-pdp-add]");

      if (priceNow) priceNow.textContent = money(v.price);
      if (priceWas) { priceWas.textContent = onSale ? "Was " + money(compare) : ""; priceWas.style.display = onSale ? "" : "none"; }
      if (priceSave) { priceSave.textContent = onSale ? "Save " + money(compare - v.price) : ""; priceSave.style.display = onSale ? "" : "none"; }
      if (klarna) klarna.textContent = money(Math.round(v.price / 4));
      if (split) split.textContent = money(Math.round(v.price / 4));
      if (total) total.textContent = money(v.price * qty);
      if (stickyPrice) stickyPrice.textContent = money(v.price * qty);
      if (stock) {
        stock.textContent = v.available ? "● In stock" : "● Out of stock";
        stock.classList.toggle("is-out", !v.available);
      }
      if (add) {
        add.disabled = !v.available;
        const label = add.querySelector("span:last-child");
        if (label) label.innerHTML = v.available ? 'Add to Basket <span class="buybox__add-sep">|</span> Free Delivery' : "Sold out";
      }
      if (v.featured_media_id) syncPdpGalleryToMedia(root, v.featured_media_id);
      document.dispatchEvent(new CustomEvent("variant:change", { detail: { variant: v } }));
    };

    rows.forEach((row) => {
      if (row.dataset.productOptionReady === "1") return;
      row.dataset.productOptionReady = "1";
      row.querySelectorAll(".vswatch, .vpill").forEach((btn) => {
        btn.addEventListener("click", () => {
          if (btn.hidden || btn.disabled || btn.classList.contains("is-storage-hidden")) return;
          row.querySelectorAll(".vswatch, .vpill").forEach((item) => {
            item.classList.remove("is-active");
            item.setAttribute("aria-checked", "false");
          });
          btn.classList.add("is-active");
          btn.setAttribute("aria-checked", "true");
          selected[row.dataset.optionRow] = btn.dataset.value;
          const label = row.querySelector("[data-selected-label]");
          if (label) label.textContent = btn.dataset.value;
          const tabValue = root.querySelector('[data-variant-tab="vtab-' + row.dataset.optionRow + '"] [data-tab-value]');
          if (tabValue) tabValue.textContent = btn.dataset.value;
          if ((row.dataset.optionName || "").includes("size")) {
            document.dispatchEvent(new CustomEvent("cosynest:variant-size-change", { detail: { size: btn.dataset.value } }));
          }
          syncStorageOptionsForSize(root, rows, selected);
          render();
          syncColorSwatches(root);
          syncColorProperty(root);
        });
      });
    });

    root.querySelectorAll("[data-qty]").forEach((btn) => {
      if (btn.dataset.qtyReady === "1") return;
      btn.dataset.qtyReady = "1";
      btn.addEventListener("click", () => {
        qty = Math.max(1, qty + (parseInt(btn.dataset.qty, 10) || 0));
        if (qtyVal) qtyVal.textContent = qty;
        if (qtyInput) qtyInput.value = qty;
        render();
      });
    });

    render();
    syncColorSwatches(root);
    syncColorProperty(root);
  }

  function normalizeOption(value) {
    return String(value || "").trim().toLowerCase();
  }

  function syncStorageOptionsForSize(root, rows, selected) {
    const sizeRow = rows.find((row) => (row.dataset.optionName || "").includes("size"));
    const storageRow = rows.find((row) => {
      const name = row.dataset.optionName || "";
      return name.includes("storage") || name.includes("drawer");
    });
    if (!sizeRow || !storageRow) return;

    const activeSize = selected[sizeRow.dataset.optionRow] || "";
    const sizeKey = normalizeOption(activeSize).replace(/\s+/g, " ");
    const compactSize = sizeKey.replace(/[^a-z0-9]/g, "");
    const restrictStorage = compactSize.includes("smallsingle") || compactSize.startsWith("single3");
    const restrictedValues = ["2 drawers end", "4 drawers"];
    let activeWasHidden = false;

    storageRow.querySelectorAll(".vswatch, .vpill").forEach((btn) => {
      const hide = restrictStorage && restrictedValues.includes(normalizeOption(btn.dataset.value));
      btn.hidden = hide;
      btn.disabled = hide;
      btn.style.display = hide ? "none" : "";
      btn.classList.toggle("is-disabled", hide);
      btn.classList.toggle("is-storage-hidden", hide);
      btn.setAttribute("aria-hidden", hide ? "true" : "false");
      btn.setAttribute("aria-disabled", hide ? "true" : "false");
      btn.tabIndex = hide ? -1 : 0;
      if (hide && btn.classList.contains("is-active")) activeWasHidden = true;
    });

    if (!activeWasHidden) return;
    const fallback =
      storageRow.querySelector('.vpill:not(.is-storage-hidden)[data-value="No Storage"], .vswatch:not(.is-storage-hidden)[data-value="No Storage"]') ||
      storageRow.querySelector(".vpill:not(.is-storage-hidden), .vswatch:not(.is-storage-hidden)");
    if (!fallback) return;

    storageRow.querySelectorAll(".vswatch, .vpill").forEach((item) => {
      item.classList.remove("is-active");
      item.setAttribute("aria-checked", "false");
    });
    fallback.classList.add("is-active");
    fallback.setAttribute("aria-checked", "true");
    selected[storageRow.dataset.optionRow] = fallback.dataset.value;
    const label = storageRow.querySelector("[data-selected-label]");
    if (label) label.textContent = fallback.dataset.value;
    const tabValue = root.querySelector('[data-variant-tab="vtab-' + storageRow.dataset.optionRow + '"] [data-tab-value]');
    if (tabValue) tabValue.textContent = fallback.dataset.value;
  }

  function colorOptionRows(root) {
    return $$("[data-option-row]", root).filter((row) => {
      const name = row.dataset.optionName || "";
      return name.includes("colour") || name.includes("color") || name.includes("fabric") || name.includes("finish");
    });
  }

  function setupColorSwatches(root) {
    const swatches = $$("[data-color-swatch]", root);
    if (!swatches.length) return;

    swatches.forEach((swatch) => {
      if (swatch.dataset.colorSwatchReady === "1") return;
      swatch.dataset.colorSwatchReady = "1";
      swatch.addEventListener("click", () => {
        const wanted = normalizeOption(swatch.dataset.value);
        if (!wanted) return;
        for (const row of colorOptionRows(root)) {
          const match = $$(".vswatch, .vpill", row).find((btn) => normalizeOption(btn.dataset.value) === wanted);
          if (match) {
            match.click();
            syncColorSwatches(root, swatch.dataset.value);
            return;
          }
        }
        /* no colour variant option to drive — reflect the pick on the swatches
           + the hidden Colour property directly */
        root.querySelectorAll("[data-color-swatch]").forEach((item) => {
          const active = normalizeOption(item.dataset.value) === wanted;
          item.classList.toggle("is-active", active);
          item.setAttribute("aria-pressed", active ? "true" : "false");
        });
        syncColorSwatches(root, swatch.dataset.value);
        syncColorProperty(root, swatch.dataset.value);
      });
    });
  }

  function syncColorSwatches(root, fallbackValue) {
    const swatches = $$("[data-color-swatch]", root);
    if (!swatches.length) return;
    const activeValues = colorOptionRows(root).map((row) => {
      const active = row.querySelector(".is-active[data-value]");
      return normalizeOption(active && active.dataset.value);
    });
    const fallback = normalizeOption(fallbackValue);
    let activeSwatch = null;
    swatches.forEach((swatch) => {
      const active = activeValues.includes(normalizeOption(swatch.dataset.value)) || (fallback && normalizeOption(swatch.dataset.value) === fallback);
      swatch.classList.toggle("is-active", active);
      swatch.setAttribute("aria-pressed", active ? "true" : "false");
      if (active) activeSwatch = swatch;
    });
    if (!activeSwatch) {
      activeSwatch = swatches[0];
      activeSwatch.classList.add("is-active");
      activeSwatch.setAttribute("aria-pressed", "true");
    }
    const activeName = activeSwatch ? activeSwatch.dataset.value || activeSwatch.getAttribute("title") || "" : "";
    const tabValue = root.querySelector('[data-variant-tab="vtab-colors"] [data-tab-value]');
    const panelLabel = root.querySelector("[data-color-swatch-label]");
    if (tabValue && activeName) tabValue.textContent = activeName;
    if (panelLabel && activeName) panelLabel.textContent = activeName;
    syncColorProperty(root, activeName);
  }

  /* Keeps the hidden properties[Colour] input in the product form in sync with
     the selection, so the custom colour lands on the cart line and renders as
     a visible line item. Priority: colour variant option → active custom
     swatch (many products have no colour option, only the swatch panel). */
  function syncColorProperty(root, activeName) {
    const input = root.querySelector('form[data-product-form] input[name="properties[Colour]"]');
    if (!input) return;
    let value = "";
    const colourRow = colorOptionRows(root)[0];
    if (colourRow) {
      const active = colourRow.querySelector(".is-active[data-value]");
      if (active) value = active.dataset.value || "";
    }
    if (!value) {
      const swatch = root.querySelector("[data-color-swatch].is-active");
      if (swatch) value = swatch.dataset.value || swatch.getAttribute("title") || "";
    }
    if (!value && activeName) value = activeName;
    input.value = value || "";
  }

  function setupPdpGallery(root) {
    const gallery = $("#pdpGallery", root);
    if (!gallery || gallery.dataset.galleryReady === "1") return;
    const mainImg = $("#pdpMainImg", gallery);
    const thumbs = $$(".gallery__thumb", gallery);
    if (!mainImg || !thumbs.length) return;
    gallery.dataset.galleryReady = "1";

    const setActive = (index) => {
      const count = thumbs.length;
      if (!count) return;
      const nextIndex = (index + count) % count;
      const thumb = thumbs[nextIndex];
      if (!thumb) return;
      thumbs.forEach((item, itemIndex) => {
        const active = itemIndex === nextIndex;
        item.classList.toggle("is-active", active);
        item.setAttribute("aria-current", active ? "true" : "false");
      });
      if (thumb.dataset.full) mainImg.src = thumb.dataset.full;
      if (thumb.dataset.alt) mainImg.alt = thumb.dataset.alt;
      thumb.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
    };

    gallery._setPdpGalleryActive = setActive;

    thumbs.forEach((thumb, index) => {
      thumb.addEventListener("click", () => setActive(index));
    });

    const prev = $("#pdpGalleryPrev", gallery);
    const next = $("#pdpGalleryNext", gallery);
    const currentIndex = () => Math.max(0, thumbs.findIndex((thumb) => thumb.classList.contains("is-active")));
    if (prev) prev.addEventListener("click", () => setActive(currentIndex() - 1));
    if (next) next.addEventListener("click", () => setActive(currentIndex() + 1));
  }

  function syncPdpGalleryToMedia(root, mediaId) {
    const gallery = $("#pdpGallery", root);
    if (!gallery || !gallery._setPdpGalleryActive || !mediaId) return;
    const thumbs = $$(".gallery__thumb", gallery);
    const index = thumbs.findIndex((thumb) => String(thumb.dataset.mediaId) === String(mediaId));
    if (index >= 0) gallery._setPdpGalleryActive(index);
  }

  function setupVariantTabs(root) {
    const wrap = root.querySelector("[data-variant-tabs]");
    if (!wrap) return;
    const tabs = $$("[data-variant-tab]", wrap);
    const panels = $$("[data-variant-panel]", wrap);
    if (!tabs.length || !panels.length) return;

    const activate = (targetId) => {
      tabs.forEach((tab) => {
        const active = tab.dataset.variantTab === targetId;
        tab.classList.toggle("is-active", active);
        tab.setAttribute("aria-selected", active ? "true" : "false");
      });
      panels.forEach((panel) => {
        const active = panel.id === targetId;
        panel.classList.toggle("is-active", active);
        panel.hidden = !active;
      });
    };

    tabs.forEach((tab) => {
      if (tab.dataset.variantTabReady === "1") return;
      tab.dataset.variantTabReady = "1";
      tab.addEventListener("click", () => activate(tab.dataset.variantTab));
    });

    const current = tabs.find((tab) => tab.classList.contains("is-active")) || tabs[0];
    if (current) activate(current.dataset.variantTab);
  }

  function setupPdpTabs(root) {
    const wraps = $$("[data-pdp-tabs]", root);
    wraps.forEach((wrap) => {
      const tabs = $$("[data-pdp-tab]", wrap);
      const panels = $$("[data-pdp-panel]", wrap);
      if (!tabs.length || !panels.length) return;

      const activate = (key) => {
        tabs.forEach((tab) => {
          const active = tab.dataset.pdpTab === key;
          tab.classList.toggle("is-active", active);
          tab.setAttribute("aria-selected", active ? "true" : "false");
        });
        panels.forEach((panel) => {
          const active = panel.dataset.pdpPanel === key;
          panel.classList.toggle("is-active", active);
          panel.hidden = !active;
        });
      };

      tabs.forEach((tab) => {
        if (tab.dataset.pdpTabReady === "1") return;
        tab.dataset.pdpTabReady = "1";
        tab.addEventListener("click", () => activate(tab.dataset.pdpTab));
      });

      const current = tabs.find((tab) => tab.classList.contains("is-active")) || tabs[0];
      if (current) activate(current.dataset.pdpTab);
    });
  }

  function setupPdpAccordions(root) {
    $$("[data-pdp-accordion]", root).forEach((wrap) => {
      const items = $$("details.pdp__accordion-item", wrap);
      if (!items.length) return;

      /* Imported descriptions (WooCommerce migrations) often contain spec rows
         with blank label AND value — hide them so the table reads clean. */
      wrap.querySelectorAll("table tr").forEach((row) => {
        if (!row.textContent.trim()) row.classList.add("is-empty");
      });

      items.forEach((item) => {
        if (item.dataset.pdpAccordionReady === "1") return;
        item.dataset.pdpAccordionReady = "1";
        item.addEventListener("toggle", () => {
          if (!item.open) return;
          items.forEach((other) => {
            if (other !== item) other.open = false;
          });
        });
      });
    });
  }

  function setupServicesAndAddons(force) {
    const root = $("[data-services-addons]");
    if (!root || (root.dataset.servicesAddonsReady === "1" && !force)) return;
    root.dataset.servicesAddonsReady = "1";

    relocateBlocks(root);
    hydrateServices();
    wireServices();
    wireAddonDrawers();
    wireAddonSelectionButtons();
    wireAddonDetails();
    wireAddonSizeSync();
    syncFromCart();
  }

  function relocateBlocks(root) {
    const addonSlot = $("[data-addon-slot]");
    const servicesSlot = $("[data-services-slot]");
    const addons = root.querySelector("[data-addons]");
    const services = root.querySelector("[data-services]");
    if (addonSlot && addons && addons.parentElement !== addonSlot) addonSlot.appendChild(addons);
    if (servicesSlot && services && services.parentElement !== servicesSlot) servicesSlot.appendChild(services);
    if ((addonSlot && addons) || (servicesSlot && services)) root.classList.add("is-relocated");
  }

  function setCheckedClass(input) {
    const card = input.closest(".service, .addon__card");
    if (!card) return;
    const group = input.closest("[data-addon-group]");
    if (group) group.querySelectorAll(".addon__card").forEach((item) => item.classList.remove("is-checked"));
    card.classList.toggle("is-checked", input.checked);
    const priceEl = card.querySelector("[data-service-price]");
    if (priceEl && input.matches("[data-service-input]")) {
      const priceText = priceEl.dataset.priceText || priceEl.textContent.replace(/^Added\s+/, "").replace(/^Add\s+/, "");
      priceEl.dataset.priceText = priceText;
      priceEl.textContent = (input.checked ? "Added " : "Add ") + priceText;
    }
  }

  function normaliseSize(value) {
    return String(value || "").toLowerCase().replace(/&quot;/g, '"').replace(/\s+/g, " ").trim();
  }
  function compactSize(value) {
    return normaliseSize(value).replace(/[^a-z0-9]/g, "");
  }
  function variantMatchesSize(variant, size) {
    const wanted = normaliseSize(size);
    const wantedCompact = compactSize(wanted);
    if (!wanted || !variant) return false;
    return [variant.option1, variant.option2, variant.option3, variant.title].some((value) => {
      const current = normaliseSize(value);
      return current === wanted || compactSize(current) === wantedCompact;
    });
  }
  function getAddonVariants(card) {
    const json = card.querySelector("[data-addon-product-variants]");
    if (!json) return [];
    if (json.dataset.parsed === "1") return json._variants || [];
    try { json._variants = JSON.parse(json.textContent || "[]"); }
    catch (e) { json._variants = []; }
    json.dataset.parsed = "1";
    return json._variants;
  }
  function applyVariantToAddonCard(card, variant) {
    const input = card && card.querySelector('input[type="radio"]');
    if (!input || !variant) return;
    input.value = variant.id;
    input.dataset.variantId = variant.id;
    input.dataset.price = variant.price || 0;
    input.disabled = !variant.available;
    card.classList.toggle("is-unavailable", !variant.available);
    const meta = card.querySelector(".addon__meta small");
    const price = card.querySelector(".addon__price");
    if (meta) meta.textContent = variant.title && variant.title !== "Default Title" ? variant.title : "Available option";
    if (price) price.textContent = variant.available ? "+" + money(variant.price || 0) : "Unavailable";
  }

  async function removeAddonGroupLines(groupId) {
    const cart = await fetch("/cart.js", { credentials: "same-origin" }).then((r) => r.json());
    const targets = cart.items.filter((it) => it.properties && String(it.properties._addon_group) === String(groupId));
    for (const item of targets) await cartPost("/cart/change.js", { id: item.key, quantity: 0 });
  }

  /* The main product's most recent cart line — the one added from THIS product
     page. Add-ons and services carry its key in a hidden _main_handle property
     so the cart renders them nested as children of that product. */
  function mainLineKey(cart) {
    const pageForm = document.querySelector("[data-product-section] form[data-product-form] input[name='id']");
    const pageVariantId = pageForm ? String(pageForm.value) : "";
    const candidates = cart.items.filter((it) => !it.properties || (!it.properties._main_handle && !it.properties._svc && !it.properties._addon_group));
    if (pageVariantId) {
      const byVariant = candidates.filter((it) => String(it.variant_id) === pageVariantId);
      if (byVariant.length) return byVariant[byVariant.length - 1].key;
    }
    return candidates.length ? candidates[candidates.length - 1].key : "";
  }

  async function addChildProperties(variantId, extraProps) {
    const cart = await fetch("/cart.js", { credentials: "same-origin" }).then((r) => r.json());
    const parentKey = mainLineKey(cart);
    if (!parentKey) return;
    const line = cart.items.find((it) => String(it.variant_id) === String(variantId) && it.properties && (it.properties._svc || it.properties._addon_group));
    if (!line) return;
    const properties = Object.assign({}, line.properties, { _main_handle: parentKey }, extraProps || {});
    await cartPost("/cart/change.js", { id: line.key, quantity: line.quantity, properties });
  }
  async function keepSingleAddonLine(groupId, variantId) {
    const cart = await fetch("/cart.js", { credentials: "same-origin" }).then((r) => r.json());
    let kept = false;
    const targets = cart.items.filter((it) => it.properties && String(it.properties._addon_group) === String(groupId));
    for (const item of targets) {
      if (!kept && String(item.variant_id) === String(variantId)) {
        kept = true;
        if (item.quantity !== 1) await cartPost("/cart/change.js", { id: item.key, quantity: 1 });
      } else {
        await cartPost("/cart/change.js", { id: item.key, quantity: 0 });
      }
    }
  }
  async function removeTaggedLine(variantId, tagKey, tagVal) {
    const cart = await fetch("/cart.js", { credentials: "same-origin" }).then((r) => r.json());
    /* remove EVERY matching line — older duplicates must not survive an uncheck */
    const targets = cart.items.filter((it) =>
      String(it.variant_id) === String(variantId) && it.properties && String(it.properties[tagKey]) === String(tagVal)
    );
    for (const target of targets) await cartPost("/cart/change.js", { id: target.key, quantity: 0 });
  }

  /* A service may only exist once — double-fires or stale duplicates must not
     stack up (each would silently add £75 to the basket). */
  async function keepSingleServiceLine(variantId) {
    const cart = await fetch("/cart.js", { credentials: "same-origin" }).then((r) => r.json());
    let kept = false;
    const targets = cart.items.filter((it) =>
      it.properties && it.properties._svc === "1" && String(it.variant_id) === String(variantId)
    );
    for (const item of targets) {
      if (!kept) { kept = true; continue; }
      await cartPost("/cart/change.js", { id: item.key, quantity: 0 });
    }
  }

  /* Invariant: a service / add-on line is only meaningful while a main product
     exists in the cart. Lines left behind (bed removed, or checkbox ticked and
     the page abandoned) neither render in the cart nor belong to any purchase,
     yet still charge the subtotal — so PDPs kept showing them as "Added" and
     they rode along with the next "Add to Basket". Relink dangling lines to the
     current main product; drop them when no main is left. Returns the number of
     lines touched so callers can refresh the drawer when needed. */
  async function cleanupOrphanAddonLines() {
    let changed = 0;
    let cart;
    try {
      cart = await fetch("/cart.js", { credentials: "same-origin" }).then((r) => r.json());
    } catch (e) { return 0; }
    const keys = new Set(cart.items.map((it) => it.key));
    const children = cart.items.filter((it) => it.properties && (it.properties._svc || it.properties._addon_group));
    if (!children.length) return 0;
    const parentKey = mainLineKey(cart);
    for (const item of children) {
      if (keys.has(item.properties._main_handle)) continue;   // linked + parent alive
      if (!parentKey) {
        await cartPost("/cart/change.js", { id: item.key, quantity: 0 });
      } else {
        const properties = Object.assign({}, item.properties, { _main_handle: parentKey });
        await cartPost("/cart/change.js", { id: item.key, quantity: item.quantity, properties });
      }
      changed++;
    }
    return changed;
  }

  function updateAddonTriggerImage(group, imageUrl) {
    const trigger = document.querySelector('[data-addon-open="' + group.dataset.panelId + '"]');
    const icon = trigger && trigger.querySelector(".addon__trigger-icon");
    if (!icon) return;
    if (!icon.dataset.defaultHtml) icon.dataset.defaultHtml = icon.innerHTML;
    if (imageUrl) {
      icon.classList.add("has-image");
      icon.innerHTML = '<img src="' + imageUrl + '" alt="" loading="lazy">';
    } else {
      icon.classList.remove("has-image");
      icon.innerHTML = icon.dataset.defaultHtml || "";
    }
  }
  function updateAddonSummary(group, input) {
    const summaryEl = document.getElementById(group.dataset.summaryId);
    const priceEl = document.getElementById(group.dataset.priceId);
    const price = parseInt(input.dataset.price, 10) || 0;
    const hasSelection = Boolean(input.dataset.variantId);
    if (summaryEl) summaryEl.textContent = hasSelection ? (input.dataset.name || "Add-on") : (group.dataset.summaryNone || "None selected");
    if (priceEl) priceEl.textContent = hasSelection ? (price > 0 ? "+" + money(price) : "Included") : (group.dataset.summaryPriceNone || "Included");
    group.dataset.selectedVariant = input.dataset.variantId || "";
    group.dataset.selectedName = input.dataset.name || "";
    updateAddonTriggerImage(group, input.dataset.image || "");
    syncAddonClearButton(group);
  }
  function clearAddonSummary(group) {
    const summaryEl = document.getElementById(group.dataset.summaryId);
    const priceEl = document.getElementById(group.dataset.priceId);
    if (summaryEl) summaryEl.textContent = group.dataset.summaryNone || "None selected";
    if (priceEl) priceEl.textContent = group.dataset.summaryPriceNone || "Included";
    group.dataset.selectedVariant = "";
    group.dataset.selectedName = "";
    group.querySelectorAll(".addon__card").forEach((card) => card.classList.remove("is-checked"));
    group.querySelectorAll('input[type="radio"]').forEach((input) => { input.checked = false; });
    updateAddonTriggerImage(group, "");
    syncAddonClearButton(group);
  }
  function syncAddonClearButton(group) {
    const clear = document.querySelector('[data-addon-clear="' + group.dataset.panelId + '"]');
    const addon = document.querySelector('[data-addon-block][data-panel-id="' + group.dataset.panelId + '"]');
    const hasSelection = Boolean(group.dataset.selectedVariant);
    if (clear) clear.hidden = !hasSelection;
    if (addon) addon.classList.toggle("is-selected", hasSelection);
  }

  function hydrateServices() {
    document.querySelectorAll("[data-service]").forEach((card) => {
      const variantId = card.dataset.variantId;
      if (!variantId || card.dataset.hydrated === "1") return;
      card.dataset.hydrated = "1";
      const priceEl = card.querySelector("[data-service-price]");
      const titleEl = card.querySelector("[data-service-title]");
      const input = card.querySelector("[data-service-input]");
      fetch("/variants/" + variantId + ".js", { credentials: "same-origin" })
        .then((r) => r.ok ? r.json() : null)
        .then((v) => {
          if (!v) return;
          if (priceEl) {
            priceEl.dataset.priceText = money(v.price);
            priceEl.textContent = (input && input.checked ? "Added " : "Add ") + priceEl.dataset.priceText;
          }
          if (titleEl && v.title && v.title !== "Default Title") titleEl.textContent = v.title;
          if (input && !v.available) { input.disabled = true; card.classList.add("is-unavailable"); }
        })
        .catch(() => { if (priceEl) priceEl.textContent = "—"; });
    });
  }

  function wireServices() {
    if (document.documentElement.dataset.servicesToggleReady === "1") return;
    document.documentElement.dataset.servicesToggleReady = "1";
    document.addEventListener("change", async (e) => {
      const input = e.target.closest("[data-service-input]");
      if (!input) return;
      if (input.dataset.pending === "1") return;
      const title = input.dataset.title || "Service";
      const card = input.closest("[data-service]");
      setCheckedClass(input);
      input.dataset.pending = "1";
      if (card) card.classList.add("is-pending");
      try {
        if (input.checked) {
          await cartPost("/cart/add.js", { id: Number(input.dataset.variantId), quantity: 1, properties: { _svc: "1", _svc_name: title } });
          await keepSingleServiceLine(input.dataset.variantId);
          await addChildProperties(input.dataset.variantId);
          toast(title + " added");
        } else {
          await removeTaggedLine(input.dataset.variantId, "_svc", "1");
          toast(title + " removed");
        }
        await refreshCart(false);
      } catch (err) {
        input.checked = !input.checked;
        setCheckedClass(input);
        toast("Sorry, that didn't work — try again");
      } finally {
        input.dataset.pending = "0";
        if (card) card.classList.remove("is-pending");
      }
    });
  }

  function wireAddonDrawers() {
    if (document.documentElement.dataset.addonDrawersReady === "1") return;
    document.documentElement.dataset.addonDrawersReady = "1";
    document.addEventListener("click", (e) => {
      const clearBtn = e.target.closest("[data-addon-clear]");
      if (clearBtn) {
        e.preventDefault();
        e.stopPropagation();
        const drawer = document.getElementById(clearBtn.dataset.addonClear);
        const group = drawer && drawer.querySelector("[data-addon-group]");
        if (!group || clearBtn.dataset.pending === "1") return;
        const removedName = group.dataset.selectedName || "Add-on";
        clearBtn.dataset.pending = "1";
        clearBtn.disabled = true;
        removeAddonGroupLines(group.id)
          .then(() => {
            clearAddonSummary(group);
            const addBtn = document.querySelector('[data-addon-add="' + group.id + '"]');
            if (addBtn) addBtn.disabled = true;
            toast(removedName + " removed");
            return refreshCart(false);
          })
          .catch(() => {
            toast("Sorry, that didn't work - try again");
            syncFromCart();
          })
          .finally(() => {
            clearBtn.dataset.pending = "0";
            clearBtn.disabled = false;
          });
        return;
      }

      const openBtn = e.target.closest("[data-addon-open]");
      if (openBtn) {
        const drawer = document.getElementById(openBtn.dataset.addonOpen);
        document.querySelectorAll("[data-addon-drawer].is-open").forEach((item) => {
          item.classList.remove("is-open");
          item.setAttribute("aria-hidden", "true");
        });
        if (drawer) {
          const detailPanel = drawer.querySelector("[data-addon-detail-panel]");
          if (detailPanel) detailPanel.hidden = true;
          drawer.classList.add("is-open");
          drawer.setAttribute("aria-hidden", "false");
          openBtn.setAttribute("aria-expanded", "true");
          document.body.classList.add("no-scroll");
        }
        return;
      }
      const closeBtn = e.target.closest("[data-addon-close]");
      if (closeBtn) {
        const drawer = closeBtn.closest("[data-addon-drawer]");
        if (drawer) {
          const detailPanel = drawer.querySelector("[data-addon-detail-panel]");
          if (detailPanel) detailPanel.hidden = true;
          drawer.classList.remove("is-open");
          drawer.setAttribute("aria-hidden", "true");
          const trigger = document.querySelector('[data-addon-open="' + drawer.id + '"]');
          if (trigger) trigger.setAttribute("aria-expanded", "false");
        }
        document.body.classList.remove("no-scroll");
      }
    });
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") document.querySelectorAll("[data-addon-drawer].is-open [data-addon-close]").forEach((btn) => btn.click());
    });
  }

  async function syncSelectedAddonAfterSize(group) {
    const input = group.querySelector('input[type="radio"]:checked[data-variant-id]:not([data-variant-id=""])');
    if (!input) return;
    updateAddonSummary(group, input);
    await removeAddonGroupLines(group.id);
    await cartPost("/cart/add.js", { id: Number(input.dataset.variantId), quantity: 1, properties: { _addon_group: group.id } });
    await keepSingleAddonLine(group.id, input.dataset.variantId);
    await addChildProperties(input.dataset.variantId);
  }
  async function syncAddonVariantsToSize(size) {
    if (!size) return;
    const changedGroups = new Set();
    document.querySelectorAll("[data-addon-group] .addon__card").forEach((card) => {
      const input = card.querySelector('input[type="radio"]');
      if (!input || input.dataset.variantId === "") return;
      const matched = getAddonVariants(card).find((variant) => variantMatchesSize(variant, size));
      if (!matched) return;
      const before = input.dataset.variantId;
      applyVariantToAddonCard(card, matched);
      if (input.checked && String(before) !== String(matched.id)) changedGroups.add(card.closest("[data-addon-group]"));
    });
    for (const group of changedGroups) {
      try {
        await syncSelectedAddonAfterSize(group);
        await refreshCart(false);
      } catch (err) {
        toast("Sorry, add-on size update failed");
      }
    }
  }
  function wireAddonSizeSync() {
    if (document.documentElement.dataset.addonSizeSyncReady === "1") return;
    document.documentElement.dataset.addonSizeSyncReady = "1";
    const sizeRow = Array.from(document.querySelectorAll("[data-option-name]")).find((row) =>
      (row.dataset.optionName || "").includes("size")
    );
    const activeSize = sizeRow && sizeRow.querySelector(".is-active[data-value]");
    if (activeSize) syncAddonVariantsToSize(activeSize.dataset.value);
    document.addEventListener("cosynest:variant-size-change", (e) => syncAddonVariantsToSize(e.detail && e.detail.size));
  }

  function wireAddonSelectionButtons() {
    document.querySelectorAll("[data-addon-group]").forEach((group) => {
      if (group.dataset.addonButtonSelectionReady === "1") return;
      group.dataset.addonButtonSelectionReady = "1";

      const addBtn = document.querySelector('[data-addon-add="' + group.id + '"]');
      const syncButton = () => {
        const selected = group.querySelector('input[type="radio"]:checked[data-variant-id]:not([data-variant-id=""])');
        if (addBtn) addBtn.disabled = !selected || selected.disabled;
      };

      group.addEventListener("change", (e) => {
        const radio = e.target.closest('input[type="radio"]');
        if (!radio || radio.disabled) return;
        setCheckedClass(radio);
        syncButton();
      });

      if (addBtn && addBtn.dataset.addonAddReady !== "1") {
        addBtn.dataset.addonAddReady = "1";
        addBtn.addEventListener("click", async () => {
          const radio = group.querySelector('input[type="radio"]:checked[data-variant-id]:not([data-variant-id=""])');
          if (!radio || radio.disabled || addBtn.dataset.pending === "1") return;
          const oldText = addBtn.textContent;
          addBtn.dataset.pending = "1";
          addBtn.disabled = true;
          addBtn.textContent = "Adding...";

          try {
            await cartPost("/cart/add.js", { id: Number(radio.dataset.variantId), quantity: 1, properties: { _addon_group: group.id } });
            await keepSingleAddonLine(group.id, radio.dataset.variantId);
            await addChildProperties(radio.dataset.variantId);
            updateAddonSummary(group, radio);
            toast((radio.dataset.name || "Add-on") + " added");
            await refreshCart(false);
            const drawer = document.getElementById(group.dataset.panelId);
            const close = drawer && drawer.querySelector("[data-addon-close]");
            if (close) close.click();
          } catch (err) {
            toast("Sorry, that didn't work - try again");
            syncFromCart();
          } finally {
            addBtn.dataset.pending = "0";
            addBtn.textContent = oldText;
            syncButton();
          }
        });
      }

      syncButton();
    });
  }

  function wireAddonDetails() {
    if (document.documentElement.dataset.addonDetailsReady === "1") return;
    document.documentElement.dataset.addonDetailsReady = "1";

    document.addEventListener("click", (e) => {
      const closeBtn = e.target.closest("[data-addon-detail-close]");
      if (closeBtn) {
        const panel = closeBtn.closest("[data-addon-detail-panel]");
        if (panel) panel.hidden = true;
        return;
      }

      const detailBtn = e.target.closest("[data-addon-detail]");
      if (!detailBtn) return;
      e.preventDefault();
      e.stopPropagation();
      const drawer = detailBtn.closest("[data-addon-drawer]");
      const panel = drawer && drawer.querySelector("[data-addon-detail-panel]");
      if (!panel) return;

      const title = panel.querySelector("[data-addon-detail-title]");
      const image = panel.querySelector("[data-addon-detail-image]");
      const price = panel.querySelector("[data-addon-detail-price]");
      const desc = panel.querySelector("[data-addon-detail-desc]");

      if (title) title.textContent = detailBtn.dataset.title || "";
      if (price) price.textContent = detailBtn.dataset.price || "";
      if (desc) desc.innerHTML = detailBtn.dataset.description || "";
      if (image) {
        if (detailBtn.dataset.image) {
          image.src = detailBtn.dataset.image;
          image.alt = detailBtn.dataset.title || "";
          image.hidden = false;
        } else {
          image.hidden = true;
        }
      }

      panel.hidden = false;
    });
  }

  function wireAddonSelection() {
    document.querySelectorAll("[data-addon-group]").forEach((group) => {
      if (group.dataset.addonSelectionReady === "1") return;
      group.dataset.addonSelectionReady = "1";
      group.addEventListener("change", async (e) => {
        const radio = e.target.closest('input[type="radio"]');
        if (!radio || radio.disabled) return;
        setCheckedClass(radio);
        try {
          if (!radio.dataset.variantId) {
            const removedName = group.dataset.selectedName || "Add-on";
            await removeAddonGroupLines(group.id);
            updateAddonSummary(group, radio);
            toast(removedName + " removed");
          } else {
            await cartPost("/cart/add.js", { id: Number(radio.dataset.variantId), quantity: 1, properties: { _addon_group: group.id } });
            await keepSingleAddonLine(group.id, radio.dataset.variantId);
            await addChildProperties(radio.dataset.variantId);
            updateAddonSummary(group, radio);
            toast((radio.dataset.name || "Add-on") + " added");
          }
          await refreshCart(false);
          const drawer = document.getElementById(group.dataset.panelId);
          if (drawer) {
            const close = drawer.querySelector("[data-addon-close]");
            if (close) close.click();
          }
        } catch (err) {
          toast("Sorry, that didn't work — try again");
          syncFromCart();
        }
      });
    });
  }

  async function syncFromCart() {
    if (syncRunning) return;           /* page load + cart-changed can overlap */
    syncRunning = true;
    try {
      /* First restore the cart invariant (orphaned services/add-ons removed or
         relinked), THEN reflect the cleaned cart in the checkboxes — otherwise
         stale phantom lines keep re-appearing as "Added" on every product. */
      const cleaned = await cleanupOrphanAddonLines();
      if (cleaned > 0) {
        await refreshCart(false);
        /* keep the /cart page in step too when cleanup ran there */
        const cartPage = document.querySelector(".cart-page");
        if (cartPage) {
          try {
            const res = await fetch("/cart?section_id=cart", { credentials: "same-origin" });
            if (res.ok) {
              const doc = new DOMParser().parseFromString(await res.text(), "text/html");
              const freshPage = doc.querySelector(".cart-page");
              if (freshPage) cartPage.replaceWith(freshPage);
            }
          } catch (e) { /* non-critical */ }
        }
      }
      const cart = await fetch("/cart.js", { credentials: "same-origin" }).then((r) => r.json());
      const svcVariants = new Set(cart.items.filter((it) => it.properties && it.properties._svc === "1").map((it) => String(it.variant_id)));
      document.querySelectorAll("[data-service-input]").forEach((input) => {
        input.checked = svcVariants.has(input.dataset.variantId);
        setCheckedClass(input);
      });

      const addonItems = new Map();
      cart.items.forEach((item) => {
        if (item.properties && item.properties._addon_group) addonItems.set(String(item.properties._addon_group), item);
      });

      document.querySelectorAll("[data-addon-group]").forEach((group) => {
        group.querySelectorAll(".addon__card").forEach((card) => card.classList.remove("is-checked"));
        group.querySelectorAll('input[type="radio"]').forEach((input) => { input.checked = false; });

        const item = addonItems.get(group.id);
        let radio = null;
        if (item) {
          radio = group.querySelector('input[data-variant-id="' + cssEscape(item.variant_id) + '"]');
          if (!radio) {
            const card = Array.from(group.querySelectorAll(".addon__card")).find((candidate) =>
              getAddonVariants(candidate).some((variant) => String(variant.id) === String(item.variant_id))
            );
            radio = card && card.querySelector('input[type="radio"]');
          }
        }
        if (!radio) {
          clearAddonSummary(group);
          const addBtn = document.querySelector('[data-addon-add="' + group.id + '"]');
          if (addBtn) addBtn.disabled = true;
          return;
        }

        radio.checked = true;
        setCheckedClass(radio);
        updateAddonSummary(group, radio);
        const addBtn = document.querySelector('[data-addon-add="' + group.id + '"]');
        if (addBtn) addBtn.disabled = !radio.dataset.variantId || radio.disabled;
        if (item && String(radio.dataset.variantId) !== String(item.variant_id)) syncSelectedAddonAfterSize(group).then(() => refreshCart(false)).catch(() => {});
        else if (item) keepSingleAddonLine(group.id, radio.dataset.variantId).catch(() => {});
      });
    } catch (e) { /* non-critical */ }
    finally { syncRunning = false; }
  }
})();
